import { parsePhoneNumber } from "libphonenumber-js";

import type {
  Channel,
  ChannelConnection,
  InstagramConfig,
  MessengerConfig,
  PhoneConfig,
  PhoneProvider,
  WhatsappConfig,
  WhatsappCoexistenceSyncState,
} from "@/types/channel";
import {
  CHANNEL_CONNECTION_STATE,
  CHANNEL_TYPE,
  ChannelType,
  PHONE_PROVIDER,
  WHATSAPP_COEXISTENCE_SYNC_STATE,
  WHATSAPP_PLATFORM_TYPE,
} from "@/types/channel";

export const getChannelIdentifier = (channel: Channel): string => {
  switch (channel.type) {
    case CHANNEL_TYPE.INSTAGRAM:
      return `@${(channel.config as InstagramConfig)?.username}`;
    case CHANNEL_TYPE.WHATSAPP:
      return formatPhoneNumberDisplay(
        (channel.config as WhatsappConfig).phoneNumber.displayPhoneNumber,
      );
    case CHANNEL_TYPE.MESSENGER:
      return `m.me/${channel.name.toLowerCase().replace(/\s+/g, "")}`;
    case CHANNEL_TYPE.TIKTOK:
      return `@${channel.name.toLowerCase().replace(/\s+/g, "")}`;
    case CHANNEL_TYPE.PHONE:
      return formatPhoneNumberDisplay(
        (channel.config as PhoneConfig).phoneNumber,
      );
    default:
      return channel.name;
  }
};

export const getChannelIdentifierLabel = (type: ChannelType): string => {
  const labels: Record<ChannelType, string> = {
    [CHANNEL_TYPE.INSTAGRAM]: "Username",
    [CHANNEL_TYPE.WHATSAPP]: "Phone Number",
    [CHANNEL_TYPE.MESSENGER]: "Page URL",
    [CHANNEL_TYPE.TIKTOK]: "Username",
    [CHANNEL_TYPE.PHONE]: "Phone Number",
  };

  return labels[type] || "Identifier";
};

export const getChannelPlatformName = (type: ChannelType): string => {
  const names: Record<ChannelType, string> = {
    [CHANNEL_TYPE.INSTAGRAM]: "Instagram",
    [CHANNEL_TYPE.WHATSAPP]: "Whatsapp",
    [CHANNEL_TYPE.MESSENGER]: "Messenger",
    [CHANNEL_TYPE.TIKTOK]: "TikTok",
    [CHANNEL_TYPE.PHONE]: "Phone",
  };

  return names[type] || "Unknown Platform";
};

export const formatPhoneNumberDisplay = (phoneNumber: string): string => {
  try {
    const parsed = parsePhoneNumber(`+${phoneNumber}`);

    return parsed ? parsed.formatInternational() : phoneNumber;
  } catch {
    return phoneNumber;
  }
};

export const getFacebookBillingHubUrl = (
  businessAccountId: string,
  businessOwnerId: string,
): string => {
  return `https://business.facebook.com/billing_hub/accounts/details/?asset_id=${businessAccountId}&business_id=${businessOwnerId}`;
};

export const getQualityRatingColor = (
  rating: string | undefined,
): "success" | "warning" | "danger" | "default" => {
  if (!rating) return "default";
  const upper = rating.toUpperCase();

  if (upper === "HIGH" || upper === "GREEN") return "success";
  if (upper === "MEDIUM" || upper === "YELLOW") return "warning";
  if (upper === "LOW" || upper === "RED") return "danger";

  return "default";
};

export const getStatusColor = (
  status: string | undefined,
): "success" | "warning" | "danger" | "default" => {
  if (!status) return "default";
  const upper = status.toUpperCase();

  if (upper === "ACTIVE" || upper === "CONNECTED") return "success";
  if (upper === "PENDING") return "warning";
  if (upper === "INACTIVE" || upper === "DISCONNECTED") return "danger";

  return "default";
};

export const getQualityRatingTextColor = (
  rating: string | undefined,
): string => {
  const colorType = getQualityRatingColor(rating);

  switch (colorType) {
    case "success":
      return "text-success";
    case "warning":
      return "text-warning";
    case "danger":
      return "text-danger";
    default:
      return "text-muted";
  }
};

export const getPhoneProviderName = (provider: PhoneProvider): string => {
  const names: Record<PhoneProvider, string> = {
    [PHONE_PROVIDER.DU]: "DU",
    [PHONE_PROVIDER.ETISALAT]: "Etisalat",
  };

  return names[provider] || "Unknown Provider";
};

export const getPhoneTypeDisplay = (isLandline: boolean): string => {
  return isLandline ? "Landline" : "Mobile";
};

export const isChannelTokenExpired = (channel: Channel): boolean => {
  return channel.config.isTokenExpired;
};

export const isCoexistenceNumber = (channel: Channel): boolean => {
  if (channel.type !== CHANNEL_TYPE.WHATSAPP) return false;

  const config = channel.config as WhatsappConfig;

  return (
    config.isCoexistence === true ||
    config.phoneNumber?.platformType === WHATSAPP_PLATFORM_TYPE.SMB_APP
  );
};

export const getCoexistenceSyncColor = (
  state: WhatsappCoexistenceSyncState | undefined,
): "success" | "warning" | "danger" | "default" => {
  switch (state) {
    case WHATSAPP_COEXISTENCE_SYNC_STATE.COMPLETED:
      return "success";
    case WHATSAPP_COEXISTENCE_SYNC_STATE.SYNCING:
      return "warning";
    default:
      return "default";
  }
};

export const getCoexistenceSyncLabel = (
  state: WhatsappCoexistenceSyncState | undefined,
): string => {
  switch (state) {
    case WHATSAPP_COEXISTENCE_SYNC_STATE.COMPLETED:
      return "Completed";
    case WHATSAPP_COEXISTENCE_SYNC_STATE.SYNCING:
      return "In progress";
    default:
      return "Not started";
  }
};

// Connection lifecycle lives inside the provider config; absent = connected.
export const getChannelConnection = (
  channel: Channel,
): ChannelConnection | undefined => {
  return (channel.config as { connection?: ChannelConnection }).connection;
};

export const isChannelDisconnected = (channel: Channel): boolean => {
  return (
    getChannelConnection(channel)?.state === CHANNEL_CONNECTION_STATE.DISCONNECTED
  );
};

const CONNECTION_REASON_COPY: Record<string, string> = {
  PRIMARY_INACTIVITY: "The WhatsApp Business app was inactive for too long",
  COMPANION_INACTIVITY: "The linked device was inactive for too long",
  BUSINESS_DOWNGRADE:
    "The number was registered with the consumer WhatsApp app",
  CHANGE_NUMBER: "The phone number was changed",
  USER_RE_REGISTERED: "The account was re-registered on a new device",
  ACCOUNT_DISCONNECTED: "The account was disconnected",
};

export const humanizeConnectionReason = (reason?: string): string => {
  const fallback = "The channel connection was lost";

  if (!reason) return fallback;

  return CONNECTION_REASON_COPY[reason.toUpperCase()] ?? fallback;
};

export const getChannelDeepLink = (channel: Channel): string | null => {
  switch (channel.type) {
    case CHANNEL_TYPE.WHATSAPP: {
      const phone = (
        channel.config as WhatsappConfig
      ).phoneNumber.displayPhoneNumber?.replace(/\D/g, "");

      return `https://wa.me/${phone}?text=Hello%20Replyn%20IO!`;
    }
    case CHANNEL_TYPE.INSTAGRAM: {
      const username = (channel.config as InstagramConfig).username;

      return `https://ig.me/m/${username}`;
    }
    case CHANNEL_TYPE.MESSENGER: {
      const pageId = (channel.config as MessengerConfig).pageId;

      return `https://m.me/${pageId}`;
    }
    case CHANNEL_TYPE.TIKTOK:
    case CHANNEL_TYPE.PHONE:
      return null;
    default:
      return null;
  }
};
